import { clamp, HintAnimation } from '../hint-motion';
import {
  abs,
  C,
  click,
  cursorTemplate,
  DIVIDER_H,
  DIVIDER_V,
  glass,
  icon,
  mountTemplate,
  MOTION,
  nextUid,
  PRESSED,
  reliefFolder,
  Refs,
  renderCamera,
  renderCursor,
  stage,
} from './scene-kit';

/**
 * Portal-Hinweis 3 „Begrenze das KI-Wissen" — Port des Claude-Design-Films
 * `KI Wissen Loop v2.dc.html` (Projekt „Portal-Hinweis 3", Datei
 * `ki-wissen-loop-v2.jsx`). Klickpfad Wissen → Reiter Wissensbasis (Ordner)
 * → Reiter Quellen & Suche → Wissens-Modus „Nur Wissensbasis".
 * Bühne 640×354, 8,0 s Loop; T = Abspielzeit / 1,6 wie bei Hinweis 1 und 2.
 */

const STRETCH = 1.6;
const AUTHORED = 5.0;

// Szenenanfänge in Autorenzeit (Summe der `nat`-Längen aus OM_SCENES).
const CUES = { Wissen: 0, Ordner: 0.7, Reiter: 1.6, Modus: 2.2, Fertig: 3.3, Loop: 4.2 };

// Geometrie: zwei Glasflächen wie im Portal (Menü links, Inhalt rechts)
const NX = 14, NY = 18, NW = 150, NH = 318;
const RXg = 174, RYg = 18, RWg = 452, RHg = 318;
const NAV: [string, string][] = [['dot', 'Allgemein'], ['gear', 'KI-Konfiguration'], ['brain', 'Wissen']];
const SEL = 2;
const DIV_AFTER = [0, 1];
const NAV_Y = (i: number) => 10 + i * 33;
const TABS: [string, string, number, number][] = [
  ['brain', 'Wissensbasis', 18, 118],
  ['searchTab', 'Quellen &amp; Suche', 140, 138],
];
const TILES = ['Prüfungen', 'Skripte WS 26', 'FAQ Studienbüro'];
const TILE_W = 128, TILE_H = 92, TILE_Y = 96;
const TILE_X = (i: number) => 20 + i * (TILE_W + 10);
const LX = 20, QX = 236, CX = 254;
const ROW_Y = 124;
const PILL = { x: CX, y: ROW_Y - 15, w: 160, h: 30 };
const MODES = ['Uneingeschränkt', 'Themenspezifisch', 'Nur Wissensbasis'];
const LIST = { x: PILL.x, y: PILL.y + PILL.h + 5, w: PILL.w, row: 28, pad: 6 };

function template(uid: string): string {
  const navRow = (ic: string, label: string, color: string, weight: number) =>
    `<div style="position:absolute;inset:0;display:flex;align-items:center;gap:8px;padding:0 10px;font-size:11.5px;font-weight:${weight};color:${color};white-space:nowrap;">` +
    `${icon(ic, 15, color === C.action ? C.action : C.icon)}${label}</div>`;
  const nav = NAV.map(
    ([ic, label], i) =>
      `<div style="${abs(8, NAV_Y(i), NW - 16, 30)}">` +
      (i === SEL ? `<div data-r="navSel" style="position:absolute;inset:0;border-radius:12px;opacity:0;${PRESSED}"></div>` : '') +
      `<div ${i === SEL ? 'data-r="navOff"' : ''} style="position:absolute;inset:0;">${navRow(ic, label, C.text, 500)}</div>` +
      (i === SEL ? `<div data-r="navOn" style="position:absolute;inset:0;opacity:0;">${navRow(ic, label, C.action, 600)}</div>` : '') +
      (DIV_AFTER.includes(i) ? `<div style="${abs(6, 31, NW - 28, 2, `background:${DIVIDER_H};`)}"></div>` : '') +
      `</div>`,
  ).join('');

  const tab = (ic: string, label: string, color: string) =>
    `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;gap:7px;font-size:12px;font-weight:500;color:${color};white-space:nowrap;">` +
    `${icon(ic, 14, color === C.action ? C.action : C.icon)}${label}</div>`;
  const tabs =
    `<div data-r="tabs" style="opacity:0;">` +
    TABS.map(
      ([ic, label, x, w]) =>
        `<div style="${abs(x, 14, w, 32)}">` +
        `<div data-r="tabOff" style="position:absolute;inset:0;">${tab(ic, label, C.text)}</div>` +
        `<div data-r="tabOn" style="position:absolute;inset:0;opacity:0;">${tab(ic, label, C.action)}</div></div>`,
    ).join('') +
    `<div style="${abs(14, 48, RWg - 72, 2, `background:${DIVIDER_H};`)}"></div>` +
    `<div data-r="tabUnder" style="${abs(TABS[0][2], 46, TABS[0][3], 2.5, `border-radius:2px;background:${C.action};`)}"></div>` +
    `</div>`;

  const close =
    `<div style="${abs(RWg - 44, 12, 30, 30, 'border-radius:999px;display:flex;align-items:center;justify-content:center;background:rgb(255 255 255 / .5);box-shadow:inset 0 1px 1px rgb(255 255 255 / .9), 0 3px 8px -5px rgb(38 65 112 / .35);')}">` +
    `${icon('close', 16, C.text, 2)}</div>`;

  const head = `<div data-r="head" style="${abs(LX, 66, 300, 20, `opacity:0;font-size:14px;font-weight:600;color:${C.text};white-space:nowrap;`)}">Wissensbasis – Verknüpfung</div>`;

  const tiles = TILES.map(
    (name, i) =>
      `<div data-r="tile" style="${abs(TILE_X(i), TILE_Y, TILE_W, TILE_H, 'opacity:0;')}">` +
      `<div style="position:absolute;inset:0;border-radius:18px;background:linear-gradient(150deg, rgb(255 255 255 / .34), rgb(255 255 255 / .12));box-shadow:inset 0 1px 1px rgb(255 255 255 / .85), inset 0 -1px 1px rgb(52 76 117 / .10);"></div>` +
      `<div style="${abs(12, 12, 32, 32)}">${reliefFolder(icon('folder', 18, C.action), '', 32)}</div>` +
      `<div style="${abs(TILE_W - 28, 14, 16, 16, `border-radius:5px;background:${C.action};box-shadow:0 3px 6px -3px rgb(37 99 235 / .6);display:flex;align-items:center;justify-content:center;`)}">${icon('check', 12, '#fff', 2.6)}</div>` +
      `<div style="${abs(12, 60, TILE_W - 16, 20, `font-size:12px;font-weight:600;color:${C.text};white-space:nowrap;`)}">${name}</div>` +
      `</div>`,
  ).join('');

  const settings =
    `<div data-r="settings" style="position:absolute;inset:0;opacity:0;">` +
    `<div style="${abs(LX, 62, RWg - 40, 16, `font-size:10.5px;font-weight:400;letter-spacing:-0.01em;color:${C.muted};white-space:nowrap;`)}">Wie der Tutor Wissensbasis und Web nutzt, und ob Kurs-spezifische Quellen aktiv sind.</div>` +
    `<div style="${abs(LX, 88, 200, 18, `font-size:13px;font-weight:500;color:${C.text};white-space:nowrap;`)}">Retrieval-Strategie</div>` +
    `<div style="${abs(LX, ROW_Y - 9, 210, 18, `display:flex;align-items:center;font-size:11px;font-weight:600;color:${C.text};white-space:nowrap;`)}">Einschränkungen der Wissensbasis</div>` +
    `<div style="position:absolute;left:${QX}px;top:${ROW_Y - 6}px;">${icon('help', 12, '#8a97ab', 1.9)}</div>` +
    `<div data-r="pill" style="${abs(PILL.x, PILL.y, PILL.w, PILL.h, 'border-radius:999px;background:rgb(255 255 255 / .62);')}">` +
    [0, 2]
      .map(
        (m) =>
          `<div data-r="pillLabel" style="${abs(14, 0, PILL.w - 40, PILL.h, `display:flex;align-items:center;font-size:12px;font-weight:500;color:${C.text};white-space:nowrap;opacity:${m === 0 ? 1 : 0};`)}">${MODES[m]}</div>`,
      )
      .join('') +
    `<div data-r="chevron" style="position:absolute;right:11px;top:7.5px;">${icon('chevron', 15)}</div>` +
    `</div></div>`;

  const saved =
    `<div data-r="saved" style="${abs(LX, 262, 200, 18, `display:flex;align-items:center;gap:8px;opacity:0;font-size:11px;font-weight:500;color:${C.muted};`)}">` +
    `<span style="width:7px;height:7px;border-radius:999px;background:${C.ok};"></span>Wird sofort gespeichert</div>`;

  const listH = LIST.pad * 2 + LIST.row * 3;
  const dropdown =
    `<div data-r="dropdown" style="${abs(LIST.x, LIST.y, LIST.w, listH, 'display:none;transform-origin:50% 0;')}">` +
    glass(
      18,
      0.95,
      'inset:0;',
      MODES.map(
        (m, i) =>
          `<div style="${abs(LIST.pad, LIST.pad + i * LIST.row, LIST.w - LIST.pad * 2, LIST.row - 2, 'display:flex;')}">` +
          (i !== 1 ? `<div data-r="opt${i}Sel" style="position:absolute;inset:0;border-radius:999px;${PRESSED}"></div>` : '') +
          `<div data-r="opt" style="position:relative;flex:1;display:flex;align-items:center;padding:0 10px;font-size:12px;font-weight:500;color:${C.text};white-space:nowrap;">${m}</div></div>`,
      ).join(''),
    ) +
    `</div>`;

  const menuGlass = glass(26, 0.42, `left:${NX}px;top:${NY}px;width:${NW}px;height:${NH}px;`, nav);
  const contentGlass = glass(
    30,
    0.42,
    `left:${RXg}px;top:${RYg}px;width:${RWg}px;height:${RHg}px;`,
    close + tabs + head + tiles + settings + saved + dropdown,
  );

  return stage(menuGlass + contentGlass + cursorTemplate(uid));
}

export class LimitKnowledgeAnimation implements HintAnimation {
  readonly width = 640;
  readonly height = 354;
  readonly playbackSeconds = AUTHORED * STRETCH;
  /** Schlussbild: Pille auf „Nur Wissensbasis", „Wird sofort gespeichert" (T = 3,9). */
  readonly stillAt = 3.9 * STRETCH;

  private refs: Refs = {};

  mount(host: HTMLElement): void {
    this.refs = mountTemplate(host, template(nextUid('al-hint-limit')));
  }

  render(t: number): void {
    this.frame(clamp(t / STRETCH, 0, AUTHORED));
  }

  private one(key: string): HTMLElement {
    return this.refs[key][0];
  }

  /** Ein Bild zur Autorenzeit T — Werte wie `Piece` in ki-wissen-loop-v2.jsx. */
  private frame(T: number): void {
    const { Wissen: wi, Ordner: od, Reiter: rq, Modus: mo, Fertig: fe, Loop: lp } = CUES;
    const live = 1 - MOTION.glide(lp, lp + 0.5, T);

    const c1 = wi + 0.48, c2 = rq + 0.16, c3 = mo + 0.24, c4 = mo + 0.62;
    const ks = [c1, c2, c3, c4].map((at) => click(at, T));
    const press = Math.max(...ks.map((k) => k.press));
    const ring = ks.map((k) => k.ring).find((r) => r > 0 && r < 1) || 0;

    const REST = [598, 318];
    const W = [NX + 60, NY + NAV_Y(SEL) + 16];
    const TB = [RXg + TABS[1][2] + 76, RYg + 32];
    const P = [RXg + PILL.x + 100, RYg + PILL.y + 16];
    const O = [RXg + LIST.x + 74, RYg + LIST.y + LIST.pad + 2 * LIST.row + 13];
    const ct = [0, wi + 0.08, c1 - 0.04, c1 + 0.1, od + 0.45, c2 - 0.04, c2 + 0.08, c3 - 0.04, c3 + 0.08, c4 - 0.06, c4 + 0.1, fe + 0.35, 99];
    const pts = [REST, REST, W, W, W, TB, TB, P, P, O, O, REST, REST];
    const cx = MOTION.path(ct, pts.map((p) => p[0]), T);
    const cy = MOTION.path(ct, pts.map((p) => p[1]), T);

    const sel = MOTION.enter(c1, c1 + 0.12, T) * live;
    const tabsO = MOTION.enter(c1 + 0.04, c1 + 0.32, T) * live;
    const sw = MOTION.glide(c2 + 0.02, c2 + 0.24, T);
    const folderOut = 1 - MOTION.enter(c2 + 0.02, c2 + 0.18, T);
    const headO = MOTION.enter(od, od + 0.24, T) * folderOut * live;
    const setO = MOTION.enter(c2 + 0.14, c2 + 0.4, T) * live;
    const open = MOTION.enter(c3, c3 + 0.18, T) * (1 - MOTION.enter(c4 + 0.08, c4 + 0.26, T));
    const pick = MOTION.enter(c4 + 0.02, c4 + 0.14, T);
    const glow = Math.sin(Math.PI * clamp((T - (c4 + 0.22)) / 0.5, 0, 1));
    const saved = MOTION.enter(c4 + 0.3, c4 + 0.5, T) * live;

    // Kamera
    const ck = [0, wi + 0.15, c1 + 0.1, od + 0.4, c2, c3 - 0.1, c4 + 0.3, lp, lp + 0.5, 99];
    const s = MOTION.path(ck, [1, 1, 1.04, 1.03, 1.04, 1.04, 1.04, 1.04, 1, 1], T);
    const fy = MOTION.path(ck, [176, 176, 160, 160, 150, 170, 170, 170, 176, 176], T);
    renderCamera(this.one('camera'), 320, fy, s);

    // Menü: „Wissen" eingedrückt und blau
    this.one('navSel').style.opacity = String(sel);
    this.one('navOff').style.opacity = String(1 - sel);
    this.one('navOn').style.opacity = String(sel);

    // Reiter: Wissensbasis → Quellen & Suche
    const tabs = this.one('tabs');
    tabs.style.opacity = String(tabsO);
    tabs.style.transform = `translateY(${(1 - tabsO) * 6}px)`;
    [1 - sw, sw].forEach((a, i) => {
      this.refs['tabOff'][i].style.opacity = String(1 - a);
      this.refs['tabOn'][i].style.opacity = String(a);
    });
    const under = this.one('tabUnder');
    under.style.left = `${TABS[0][2] + (TABS[1][2] - TABS[0][2]) * sw}px`;
    under.style.width = `${TABS[0][3] + (TABS[1][3] - TABS[0][3]) * sw}px`;

    // Reiter Wissensbasis: verknüpfte Ordner
    this.one('head').style.opacity = String(headO);
    this.refs['tile'].forEach((el, i) => {
      const st = od + 0.04 + 0.14 * i;
      const o = MOTION.enter(st, st + 0.3, T) * folderOut * live;
      el.style.opacity = String(o);
      el.style.transform = `translateY(${(1 - o) * 10}px) scale(${0.96 + 0.04 * o})`;
    });

    // Reiter Quellen & Suche: Wissens-Modus
    const settings = this.one('settings');
    settings.style.opacity = String(setO);
    settings.style.transform = `translateY(${(1 - setO) * 6}px)`;
    this.one('pill').style.boxShadow =
      `inset 0 1px 1px rgb(255 255 255 / .9), 0 0 0 1px rgb(83 109 145 / ${0.18 + 0.25 * open}), ` +
      `0 3px 8px -5px rgb(38 65 112 / .3), 0 0 0 ${2 * glow}px rgb(59 130 246 / ${0.5 * glow}), 0 0 ${16 * glow}px rgb(59 130 246 / ${0.4 * glow})`;
    this.refs['pillLabel'][0].style.opacity = String(1 - pick);
    this.refs['pillLabel'][1].style.opacity = String(pick);
    this.one('chevron').style.transform = `rotate(${180 * open}deg)`;

    // Auswahlliste
    const dropdown = this.one('dropdown');
    if (open <= 0.001) {
      dropdown.style.display = 'none';
    } else {
      dropdown.style.display = 'block';
      dropdown.style.opacity = String(open);
      dropdown.style.transform = `translateY(${(1 - open) * -6}px) scale(${0.97 + 0.03 * open})`;
      this.one('opt0Sel').style.opacity = String(1 - pick);
      this.one('opt2Sel').style.opacity = String(pick);
      this.refs['opt'].forEach((el, i) => {
        el.style.fontWeight = (i === 2 && pick > 0.5) || (i === 0 && pick <= 0.5) ? '600' : '500';
      });
    }

    // „Wird sofort gespeichert"
    const savedEl = this.one('saved');
    savedEl.style.opacity = String(saved);
    savedEl.style.transform = `translateY(${(1 - saved) * 4}px)`;

    renderCursor(this.one('ring'), this.one('arrow'), cx, cy, press, ring);
  }
}
